import {ActionContext, ActionTree, GetterTree, Module, MutationTree} from 'vuex'
import {AppState} from '@/stores'
import {I18n} from 'vue-i18n'
import {SiteManagerDeliveryRequestBean, SiteManagerOrderApi, DisputeDto, CreateDeliveryRequestV2Bean, ConfirmDeliveryRequestV2Bean} from '@/service/api/sitemanager/SiteManagerOrderApi'
import {CustomerOrderDetailBean} from '@/service/model/sitemanager/CustomerOrderBean'
import {DeliveryNoteBean} from '@/service/model/DeliveryNoteBean'
import {makeException} from '@/exception/Exception'
import {translateMessage} from '@/i18n'
import {PictureBean} from '@/service/model/PictureBean'

export const deliveryRequestOrderCodePrefix = 'DRO-'
export const deliveryRequestPageCodePrefix = 'DRP-'

export interface SiteManagerDeliveryRequestModuleState {
  _placeholder?: undefined
}

export class SiteManagerDeliveryRequestModule implements Module<SiteManagerDeliveryRequestModuleState, AppState> {
  namespaced = true
  state: SiteManagerDeliveryRequestModuleState = {}
  actions: ActionTree<SiteManagerDeliveryRequestModuleState, AppState> = {
    fetchOrder: this.fetchOrderAction.bind(this),
    fetchDeliveryRequests: this.fetchDeliveryRequestsAction.bind(this),
    getDeliveryRequest: this.getDeliveryRequestAction.bind(this),
    getDeliveryRequestWithDeliveryNote: this.getDeliveryRequestWithDeliveryNoteAction.bind(this),
    confirmDelivery: this.confirmDeliveryAction.bind(this),
    fetchDeliveryRequestByCode: this.fetchDeliveryRequestByCodeAction.bind(this),
    isDeliveryRequestCode: this.isDeliveryRequestCodeAction.bind(this),
    disputeDeliveryRequest: this.disputeDeliveryRequestAction.bind(this),
    createDeliveryRequestV2: this.createDeliveryRequestV2Action.bind(this),
    fetchAllDeliveryRequestsV2: this.fetchAllDeliveryRequestsV2Action.bind(this),
    confirmDeliveryV2: this.confirmDeliveryV2Action.bind(this),
  }
  mutations: MutationTree<SiteManagerDeliveryRequestModuleState> = {}
  getters: GetterTree<SiteManagerDeliveryRequestModuleState, AppState> = {}

  constructor(
    private readonly i18n: I18n,
    private readonly orderApi: SiteManagerOrderApi
  ) {
  }

  async fetchDeliveryRequestsAction(
    _context: ActionContext<SiteManagerDeliveryRequestModuleState, AppState>,
    type: string
  ): Promise<Array<SiteManagerDeliveryRequestBean>> {
    const deliveryRequest = await this.orderApi.fetchDeliveryRequests(type)
    if (!deliveryRequest) {
      throw makeException(this.i18n, 'deliveryRequest.unknown')
    }
    return deliveryRequest
  }

  async getDeliveryRequestAction(
    _context: ActionContext<SiteManagerDeliveryRequestModuleState, AppState>,
    iddr: string
  ): Promise<SiteManagerDeliveryRequestBean> {
    const deliveryRequest = await this.orderApi.getDeliveryRequestV2(iddr)
    if (!deliveryRequest) {
      throw makeException(this.i18n, 'deliveryRequest.unknown')
    }
    return deliveryRequest
  }

  async getDeliveryRequestWithDeliveryNoteAction(
    _context: ActionContext<SiteManagerDeliveryRequestModuleState, AppState>,
    params: {deliveryRequestId: string; materialId: string}
  ): Promise<Array<DeliveryNoteBean>> {
    try {
      return await this.orderApi.getDeliveryRequestWithDeliveryNote({
        deliveryRequestId: params.deliveryRequestId,
        materialId: params.materialId
      })
    } catch (error) {
      console.error('Error requesting DeliveryRequestWithDeliveryNote:', error)
      throw error
    }
  }

  async confirmDeliveryAction(
    _context: ActionContext<SiteManagerDeliveryRequestModuleState, AppState>,
    id: string
  ) {
    try {
      await this.orderApi.confirmDelivery(id)
    } catch (error) {
      console.error('Error requesting DeliveryRequest:', error)
      throw error
    }
  }

  async fetchOrderAction(
    _context: ActionContext<SiteManagerDeliveryRequestModuleState, AppState>,
    id: string
  ): Promise<CustomerOrderDetailBean> {
    return this.orderApi.fetchOrderByIdV2(id)
  }

  async fetchDeliveryRequestByCodeAction(
    _context: ActionContext<SiteManagerDeliveryRequestModuleState, AppState>,
    siteManagerCode: string
  ): Promise<SiteManagerDeliveryRequestBean> {
    const deliveryRequest = await this.orderApi.fetchDeliveryRequestByCode(siteManagerCode)
    if (!deliveryRequest) {
      throw makeException(this.i18n, 'auth.noDeliveryRequestAssociatedToCode')
    }
    return deliveryRequest
  }

  isDeliveryRequestCodeAction(
    _context: ActionContext<SiteManagerDeliveryRequestModuleState, AppState>,
    siteManagerCode: string
  ): boolean {
    return siteManagerCode.startsWith(deliveryRequestOrderCodePrefix) || siteManagerCode.startsWith(deliveryRequestPageCodePrefix)
  }

  async disputeDeliveryRequestAction(
    context: ActionContext<SiteManagerDeliveryRequestModuleState, AppState>,
    params: {id: string; dto: DisputeDto; picture: PictureBean}
  ): Promise<void> {
    try {
      await this.orderApi.disputeDeliveryRequest({
        id: params.id,
        disputeDto: params.dto,
        picture: params.picture
      })
      await context.dispatch(
        'message/setMessage',
        {message: translateMessage(this.i18n, 'deliveryRequest.dispute')},
        {root: true}
      )
    } catch (error) {
      console.error('Error requesting dispute:', error)
      throw error
    }
  }

  async createDeliveryRequestV2Action(
    _context: ActionContext<SiteManagerDeliveryRequestModuleState, AppState>,
    bean: CreateDeliveryRequestV2Bean
  ): Promise<{id: string}> {
    const result = await this.orderApi.createDeliveryRequestV2(bean)
    await _context.dispatch('siteManager/invalidateMutationCaches', undefined, {root: true})
    return result
  }

  async fetchAllDeliveryRequestsV2Action(
    _context: ActionContext<SiteManagerDeliveryRequestModuleState, AppState>, // eslint-disable-line @typescript-eslint/no-unused-vars
    orderId?: string
  ): Promise<Array<SiteManagerDeliveryRequestBean>> {
    return this.orderApi.fetchAllDeliveryRequestsV2(orderId)
  }

  async confirmDeliveryV2Action(
    _context: ActionContext<SiteManagerDeliveryRequestModuleState, AppState>,
    params: { id: string; dto: ConfirmDeliveryRequestV2Bean }
  ): Promise<void> {
    await this.orderApi.confirmDeliveryV2(params.id, params.dto)
  }
}
